import React from 'react';
import { FreeVisual } from './FreeVisual';

/**
 * Free-positioned object visual (image, rich_text, shape) rendered on the dashboard
 * grid canvas. Uses the same FreeVisual wrapper as the report-mode zones, but with
 * dashboard-mode default position fallbacks and richer shape variants.
 *
 * Shape variants supported:
 *   - 'rectangle' (default), 'circle', 'line_h', 'line_v'
 *
 * Rich_text supports vertical alignment (textVAlign) which the page-zone variant doesn't.
 */
export function FreeObjectVisual({
  v,
  isActive, onSelect,
  onUpdate, onContextMenu,
  setSavedClean,
}) {
  const fp = v.freePosition || { x: 50 + Math.random() * 50, y: 50 + Math.random() * 50, w: 200, h: 150 };

  return (
    <FreeVisual
      position={fp}
      zIndex={v.zIndex || 10}
      isActive={isActive}
      onSelect={onSelect}
      onPositionChange={(newPos) => { onUpdate({ freePosition: newPos }); setSavedClean(false); }}
      onContextMenu={onContextMenu}
    >
      {v.vizType === 'image' && (
        <div className="w-full h-full flex items-center justify-center">
          {v.imageUrl
            ? <img src={v.imageUrl} alt={v.title} className={'max-w-full max-h-full ' + (v.imageBorder ? 'border-2 border-border/50 rounded-lg ' : '') + 'object-' + (v.imageFit || 'contain')} />
            : <div className="w-full h-full rounded-lg border-2 border-dashed border-border/30 flex items-center justify-center"><p className="text-xs text-muted-foreground/70 italic">Set image URL in Visuals tab</p></div>}
        </div>
      )}
      {v.vizType === 'rich_text' && (
        <div
          className={`w-full h-full p-1 overflow-auto flex ${v.textVAlign === 'middle' ? 'items-center' : v.textVAlign === 'bottom' ? 'items-end' : 'items-start'}`}
          onClick={e => e.stopPropagation()}
        >
          <textarea
            value={v.textContent || ''}
            onChange={e => onUpdate({ textContent: e.target.value })}
            className="w-full flex-1 bg-transparent border-none outline-none resize-none p-0 m-0"
            style={{
              fontSize: v.textSize || 16,
              color: v.textColor || 'inherit',
              textAlign: v.textAlign || 'left',
              fontWeight: v.textBold ? 'bold' : 'normal',
              fontStyle: v.textItalic ? 'italic' : 'normal',
              lineHeight: 1.2,
            }}
            placeholder="Type here..."
          />
        </div>
      )}
      {v.vizType === 'shape' && <ShapeRenderer shapeType={v.shapeType} fill={v.shapeFill} opacity={v.shapeOpacity} border={v.shapeBorder} borderRadius={v.shapeBorderRadius} />}
    </FreeVisual>
  );
}

/**
 * Renders the inner shape variants — rectangle (default), circle, horizontal line, vertical line.
 * Centralizes the duplicated shape JSX that lived in two places in ReportBuilderPage.
 */
function ShapeRenderer({ shapeType, fill = '#3b82f6', opacity = 0.3, border = '#1e40af', borderRadius = 12 }) {
  if (shapeType === 'circle') {
    return (
      <div className="w-full h-full" style={{ background: fill, opacity, border: `2px solid ${border}`, borderRadius: '50%' }} />
    );
  }
  if (shapeType === 'line_h') {
    return <div className="w-full" style={{ height: 3, background: fill, opacity: opacity ?? 1, marginTop: '50%' }} />;
  }
  if (shapeType === 'line_v') {
    return <div className="h-full" style={{ width: 3, background: fill, opacity: opacity ?? 1, marginLeft: '50%' }} />;
  }
  // 'rectangle' (default)
  return (
    <div className="w-full h-full" style={{ background: fill, opacity, border: `2px solid ${border}`, borderRadius }} />
  );
}
